import { Button } from '@trellis/roots/button'
import { DetailCard } from '@trellis/roots/data'
import { Typography } from '@trellis/roots/typography'
import { ExternalLink, FileText, History } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  useInvoicePdfSignedUrlMutation,
  useInvoicePdfVersionsQuery,
  useQuotePdfSignedUrlMutation,
  useQuotePdfVersionsQuery,
} from '@/hooks'
import { downloadFile } from '@/lib/download-file'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'

type PdfVersionHistoryProps =
  | { entityType: 'invoice'; entityId: string }
  | { entityType: 'quote'; entityId: string }

export function PdfVersionHistory({ entityType, entityId }: PdfVersionHistoryProps) {
  const [pendingVersionId, setPendingVersionId] = useState<string | null>(null)

  const invoiceVersionsQuery = useInvoicePdfVersionsQuery(
    entityType === 'invoice' ? entityId : '',
  )
  const quoteVersionsQuery = useQuotePdfVersionsQuery(
    entityType === 'quote' ? entityId : '',
  )
  const invoiceSignedUrlMutation = useInvoicePdfSignedUrlMutation()
  const quoteSignedUrlMutation = useQuotePdfSignedUrlMutation()

  const versionsQuery =
    entityType === 'invoice' ? invoiceVersionsQuery : quoteVersionsQuery
  const versions = versionsQuery.data?.data ?? []

  function handleOpen(versionId: string) {
    setPendingVersionId(versionId)

    const onSuccess = (result: { data: { url: string; filename: string } | null }) => {
      setPendingVersionId(null)
      if (!result.data) return
      downloadFile(result.data.url, result.data.filename).catch(
        (caughtError: unknown) => {
          const message =
            caughtError instanceof Error ? caughtError.message : 'Could not download PDF'
          toast.error('Error', { description: message })
        },
      )
    }
    const onError = (caughtError: unknown) => {
      setPendingVersionId(null)
      const message =
        caughtError instanceof Error ? caughtError.message : 'Could not open PDF'
      const isExpired =
        message.toLowerCase().includes('expired') ||
        message.toLowerCase().includes('no longer')
      toast.error(isExpired ? 'PDF expired' : 'Error', {
        description: isExpired
          ? 'This version is no longer on storage. Generate a new PDF to continue.'
          : message,
      })
    }

    if (entityType === 'invoice') {
      invoiceSignedUrlMutation.mutate(
        { invoiceId: entityId, versionId },
        { onSuccess, onError },
      )
    } else {
      quoteSignedUrlMutation.mutate(
        { quoteId: entityId, versionId },
        { onSuccess, onError },
      )
    }
  }

  if (versions.length === 0) return null

  return (
    <DetailCard title="PDF History" icon={History} contentClassName="px-5 py-2">
      {versions.map((version, versionIdx) => (
        <div
          key={version.id}
          className={cn(
            'flex items-center gap-2.5 py-2.5',
            versionIdx !== versions.length - 1 && 'border-b',
          )}
        >
          <div className="bg-muted flex size-[26px] shrink-0 items-center justify-center rounded-full">
            <FileText className="size-3" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <Typography variant="body-sm" className="font-medium">
              v{version.versionNumber}
              {!version.isArchived && (
                <Typography variant="label" as="span" color="muted" className="ml-1.5">
                  current
                </Typography>
              )}
            </Typography>
            <Typography variant="label" color="muted" className="truncate tabular-nums">
              {formatDate(version.generatedAt, { dateStyle: 'medium' })} ·{' '}
              {version.generatedBy.name}
            </Typography>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            aria-label="Open"
            onClick={() => handleOpen(version.id)}
            disabled={pendingVersionId === version.id}
          >
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      ))}
    </DetailCard>
  )
}
